import React, { useState } from 'react'
import { Alert, Dropdown, MenuProps, notification, Typography } from 'antd'
import Icon from '@ant-design/icons'
import { createPortal } from 'react-dom'
import { createSelector } from '@reduxjs/toolkit'

import './index.less'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import logo_horizontal from '../../../assets/logo_horizontal.svg'
import { arrowDownIcn, docsIcn, keypairIcn, logOutIcn, manageUserIcn, shareIcn, userIcn } from '../../../assets/CustomIcons'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CardinalApiState, logout } from '../../../core/services/auth.api'
import { useCreateOrUpdatePractitionerMutation, useGetPractitionerQuery } from '../../../core/api/practitionerApi'
import { ModalManageAccountForm } from '../../user/ModalManageAccountForm'
import { getImgSRC } from '../../../helpers/fileToBase64'
import { ModalConfirmAction } from '../ModalConfirmAction'
import { hexToUint8Array, int8ArrayToHex } from '../../../helpers/hash'
import { ModalMsCredential } from './ModalMsCredential'
import { ModalManageFormTemplates } from './ModalManageFormTemplates'
import { AuthenticationMethod, CardinalBaseSdk, CryptoService, HealthcareParty, RsaEncryptionAlgorithm } from '@icure/cardinal-sdk'
import { ICURE_CLOUD_URL, SMALL_MODAL_WIDTH } from '../../../constants'
import { useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation } from '../../../core/api/interopApi'
import { useCreateRecoveryInfoForAvailableKeyPairsMutation } from '../../../core/api/userApi'
import { CustomModal } from '../CustomModal'

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    user: cardinalApi.user,
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
  }),
)

export const Header = () => {
  const [isUserDropdownOpen, setUserDropdownOpen] = useState(false)
  const [isModalManageAccountFormOpen, setModalManageAccountFormOpen] = useState(false)
  const [providedRecoveryKey, setProvidedRecoveryKey] = useState<string | undefined>(undefined)
  const [isMsDialogOpen, setMsDialogOpen] = useState(false)
  const [isFormTemplatesModalOpen, setFormTemplatesModalOpen] = useState(false)
  const dispatch = useAppDispatch()

  const [giveAccessToNewPrivateKey] = useGiveAccessToNewHealthcarePartyWithPrivateKeyMutation()
  const [createRecoveryInfoForAvailableKeyPairs] = useCreateRecoveryInfoForAvailableKeyPairsMutation()

  const { user, healthcarePartyId } = useAppSelector(reduxSelector)

  const { data: practitioner, isFetching: isPractitionerFetching } = useGetPractitionerQuery(healthcarePartyId ?? '', { skip: !healthcarePartyId })

  const notify = (text: string, type: 'success' | 'error' = 'success') => {
    notification[type]({
      type: type,
      message: text,
      duration: 30,
    })
  }

  const userAvatarSrc = getImgSRC(practitioner?.picture)
  const handleLogout = () => {
    dispatch(logout())
  }

  const handleprovideRecoveryKeys = () => {
    createRecoveryInfoForAvailableKeyPairs()
      .unwrap()
      .then((recoveryKey) => {
        setProvidedRecoveryKey(recoveryKey)
      })
  }

  const handleLinkToMsInstance = () => {
    setMsDialogOpen(true)
  }

  // Modify the MS database with Preventi data
  // Download the user's private key from the Cardinal SDK and trigger a file download
  const handleMsDialogConfirm = async (values: Record<string, any>) => {
    setMsDialogOpen(false)

    const login = values['msLogin'] as string
    const password = values['msPassword'] as string

    giveAccessToNewPrivateKey({ username: login, password })
      .unwrap()
      .then(() => {
        notify('Private key successfully added to Preventi.')
      })
      .catch((error: Error) => {
        console.error('Failed to add private key:', error)
        notify(error.message, 'error')
      })
  }

  const items: MenuProps['items'] = [
    {
      key: 'manageAccount',
      label: (
        <div className="header__userDropdown__item">
          <Icon component={manageUserIcn} />
          <span>Manage account</span>
        </div>
      ),
    },
    {
      key: 'formTemplates',
      label: (
        <div className="header__userDropdown__item">
          <Icon component={docsIcn} />
          <span>Form templates</span>
        </div>
      ),
    },
    {
      key: 'provideRecoveryKeys',
      label: (
        <div className="header__userDropdown__item">
          <Icon component={keypairIcn} />
          <span>Your Recovery Key</span>
        </div>
      ),
    },
    {
      key: 'linkToMsInstance',
      label: (
        <div className="header__userDropdown__item">
          <Icon component={shareIcn} />
          <span>Link to MS instance</span>
        </div>
      ),
    },
    {
      key: 'logout',
      danger: true,
      label: (
        <div className="header__userDropdown__item">
          <Icon component={logOutIcn} />
          <span>Log out</span>
        </div>
      ),
    },
  ]
  const onClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'manageAccount': {
        setModalManageAccountFormOpen(true)
        break
      }
      case 'formTemplates': {
        setFormTemplatesModalOpen(true)
        break
      }
      case 'logout': {
        handleLogout()
        break
      }
      case 'provideRecoveryKeys': {
        handleprovideRecoveryKeys()
        break
      }
      case 'linkToMsInstance': {
        handleLinkToMsInstance()
        break
      }
    }
  }

  return (
    <>
      {/*<div className="headerBanner">*/}
      {/*  <p>*/}
      {/*  </p>*/}
      {/*</div>*/}

      <div className="header">
        <div className="header__logoHolder">
          <img src={logo_horizontal} alt="preventi logo" />
        </div>
        {!isPractitionerFetching && (
          <Dropdown menu={{ items, onClick }} placement="bottomRight" arrow onOpenChange={(open: boolean) => setUserDropdownOpen(open)}>
            <div className={`header__userDropdown ${isUserDropdownOpen && 'header__userDropdown--active'}`}>
              <div className="header__userDropdown__heading">
                <p className="header__userDropdown__heading__name">{practitioner?.firstName + ' ' + practitioner?.lastName}</p>
                <p className="header__userDropdown__heading__speciality">{practitioner?.speciality ?? 'Speciality'}</p>
              </div>
              {userAvatarSrc ? (
                <div className="header__userDropdown__picture">
                  <img src={userAvatarSrc} alt={user?.name ?? 'Dear User!'} />
                </div>
              ) : (
                <div className="header__userDropdown__userAvatarPlaceholder">
                  <Icon component={userIcn} />
                </div>
              )}
              <div className="header__userDropdown__arrow">
                <Icon component={arrowDownIcn} />
              </div>
            </div>
          </Dropdown>
        )}
      </div>
      {isModalManageAccountFormOpen &&
        createPortal(
          <ModalManageAccountForm isVisible={isModalManageAccountFormOpen} onClose={() => setModalManageAccountFormOpen(false)} practitionerToBeUpdated={practitioner} />,
          document.body,
        )}
      {providedRecoveryKey &&
        createPortal(
          <CustomModal
            mode={'danger'}
            isVisible={!!providedRecoveryKey}
            handleClose={() => setProvidedRecoveryKey(undefined)}
            handleClickPrimaryBtn={() => setProvidedRecoveryKey(undefined)}
            primaryBtnTitle={'Close'}
            title={'Your Recovery Key'}
            width={SMALL_MODAL_WIDTH}
          >
            <div className="modalConfirmAction">
              <Alert
                message={'Copy this recovery key to another computer to give it access to your encrypted data:'}
                description={<Typography.Paragraph copyable={true}>{providedRecoveryKey}</Typography.Paragraph>}
              />
            </div>
          </CustomModal>,
          document.body,
        )}
      <ModalMsCredential
        visible={isMsDialogOpen}
        handleClose={() => setMsDialogOpen(false)}
        onFinish={async (values) => {
          await handleMsDialogConfirm(values)
        }}
      />
      {isFormTemplatesModalOpen &&
        createPortal(
          <ModalManageFormTemplates isVisible={isFormTemplatesModalOpen} onClose={() => setFormTemplatesModalOpen(false)} />,
          document.body,
        )}
    </>
  )
}
